import React from 'react';
import MinimalLayout from '../components/layout/MinimalLayout';
import TeacherCalendar from '../components/TeacherCalendar';
import { CalendarIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';

const CalendarDemoPage: React.FC = () => {
  return (
    <MinimalLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                  <CalendarIcon className="w-8 h-8 mr-3 text-blue-600" />
                  Calendar Integration Demo
                </h1>
                <p className="text-gray-600 mt-1">
                  See how LessonLynx helps music teachers optimize their schedules
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          
          {/* Features Overview */}
          <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="flex items-center mb-4">
                <CheckCircleIcon className="w-8 h-8 text-green-600 mr-3" />
                <h3 className="text-lg font-semibold">Smart Scheduling</h3>
              </div>
              <p className="text-gray-600">
                AI suggests optimal time slots to minimize gaps between lessons, maximizing your teaching efficiency.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="flex items-center mb-4">
                <CalendarIcon className="w-8 h-8 text-blue-600 mr-3" />
                <h3 className="text-lg font-semibold">Google Calendar Sync</h3>
              </div>
              <p className="text-gray-600">
                Automatic two-way sync with Google Calendar. Students get calendar invites, you get mobile notifications.
              </p>
            </div>
            
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="flex items-center mb-4">
                <ClockIcon className="w-8 h-8 text-purple-600 mr-3" />
                <h3 className="text-lg font-semibold">Gap Optimization</h3>
              </div>
              <p className="text-gray-600">
                See exactly where you can fit lessons without breaks. Perfect for musicians with complex schedules.
              </p>
            </div>
          </div>

          {/* Demo Notice */}
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarIcon className="w-5 h-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">
                  Live Calendar Demo
                </h3>
                <div className="mt-1 text-sm text-blue-700">
                  This is a working demo showing how the calendar integration helps music teachers manage their schedules efficiently. 
                  The data below shows a typical week for a piano teacher with smart scheduling suggestions.
                </div>
              </div>
            </div>
          </div>

          {/* Calendar Component */}
          <TeacherCalendar />

          {/* Key Benefits */}
          <div className="mt-8 bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Why Teachers Love This Calendar</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">🎯 Perfect for Musicians</h4>
                <ul className="text-gray-600 space-y-1 text-sm">
                  <li>• Handles complex schedules (band practice, gigs, teaching)</li>
                  <li>• Shows optimal teaching blocks to minimize travel</li>
                  <li>• Suggests back-to-back lessons to maximize earnings</li>
                  <li>• Respects your availability preferences</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-2">📱 Professional Integration</h4>
                <ul className="text-gray-600 space-y-1 text-sm">
                  <li>• Syncs with Google Calendar automatically</li>
                  <li>• Students receive calendar invites with lesson details</li>
                  <li>• Mobile notifications for upcoming lessons</li>
                  <li>• Works with your existing calendar workflow</li>
                </ul>
              </div>
            </div>

            <div className="mt-6 p-4 bg-green-50 rounded-lg">
              <div className="flex items-center">
                <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
                <span className="font-medium text-green-800">Real Example:</span>
              </div>
              <p className="text-green-700 mt-1 text-sm">
                Sarah teaches piano and sees that scheduling a 5:30 PM lesson on Monday would eliminate the gap after her 4:30 PM lesson, 
                allowing her to finish teaching by 6:30 PM instead of having scattered lessons throughout the evening.
              </p>
            </div>
          </div>
        </div>
      </div>
    </MinimalLayout>
  );
};

export default CalendarDemoPage; 